/**
 * PaymentDetectionCard — o interruptor "detectar pagamento automaticamente".
 *
 * Um interruptor só, e o texto abaixo dele SEMPRE diz o que está valendo hoje —
 * inclusive quando ligado não é suficiente (conta em análise, sem chave de
 * repasse). Um interruptor ligado que continua imprimindo o QR de sempre, sem
 * explicação, lê como bug do app; é a mesma razão pela qual o relatório mostra
 * quantos itens ficaram de fora de uma conta (#61) em vez de calar.
 *
 * A escolha aplica na hora, sem botão Salvar: é um booleano, e um rascunho com
 * "Salvar" só criaria o estado intermediário em que a tela e o banco divergem.
 *
 * ⚠️ O bloco NÃO APARECE PARA NINGUÉM hoje — nem no grátis, nem no PRO —, e o
 * app lança assim. `visible` é `canSeeFinance && !feature.blocked`, e a feature
 * está `blocked` nos dois planos até o BaaS do Asaas ser habilitado em produção
 * (ver o comentário em `features.ts`, que explica por que isso pode nunca
 * acontecer). Ligar o interruptor cria uma subconta REAL e dispara o período de
 * avaliação regulatória.
 *
 * Foi decidido em 2026-08-07 NÃO anunciar a feature como "em breve": a data
 * depende da aprovação de um terceiro, e um card que promete o que o app não
 * controla é a mesma mentira do toast de sucesso enganoso (#9). Enquanto isso o
 * Pix estático segue grátis e inalterado — nada foi retirado de ninguém.
 *
 * O que sobrou aqui é o caminho de TESTE (admin / `feature_overrides`), que
 * continua exercitando a estrutura já provada ponta a ponta em sandbox.
 */
import { QrCode, Loader2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { usePaymentDetection } from '@/hooks/usePaymentDetection';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { AsaasSeal } from '@/components/payments/AsaasSeal';
import { ASAAS_SHORT_DISCLOSURE, ASAAS_SUPPORT } from '@/lib/asaasDisclosure';
import { PIX_QR_MODE_HINTS } from '@/lib/paymentDetection';

export function PaymentDetectionCard() {
  const { enabled, resolution, canToggle, visible, loading, toggling, setEnabled } =
    usePaymentDetection();
  const navigate = useNavigate();
  // Mesmo gate SERVIDOR de `asaas-onboard` (`resolveEntitlement`), lido aqui
  // direto — `usePaymentDetection` não expõe `feature.allowed` porque o
  // BLOCO inteiro depende também de `autoDetectionReachable()` (o
  // congelamento, addendum do #165). O link de sandbox abaixo NÃO pode
  // depender dessa segunda trava: ela existe para calar o bloco de
  // PRODUÇÃO, e aplicá-la aqui tornaria o próprio caminho de teste
  // inalcançável — o oposto do que a decisão de sessão de 2026-08-10 pediu.
  const feature = useFeature('auto_payment_detection');
  const { isAdmin } = useRole();

  if (!visible) {
    // O bloco de produção está calado (congelamento) ou o usuário não vê
    // financeiro. Só sobra o caminho de TESTE, e só para quem o servidor
    // também deixaria passar (plano admin/override + papel owner/admin).
    if (!feature.allowed || !isAdmin) return null;
    return (
      <div className="rounded-xl border border-dashed border-border bg-card p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Ambiente de teste (sandbox)
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Construção da subconta Asaas para gerar evidência do playbook de BaaS.
          Não afeta o BaaS de produção, que continua congelado.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="mt-2"
          onClick={() => navigate('/pagamentos/conectar-teste')}
        >
          Conectar conta de pagamento
        </Button>
      </div>
    );
  }

  // Ligado mas ainda não vale: é este par que a tela precisa distinguir. Sem
  // ele, "ligado" e "funcionando" viram a mesma coisa aos olhos do usuário.
  const pendingSetup = enabled && !resolution.dynamic && resolution.reason !== 'feature_blocked';

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start gap-4">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
            resolution.dynamic ? 'bg-primary/15' : 'bg-accent'
          }`}
        >
          <QrCode
            className={`h-4 w-4 ${resolution.dynamic ? 'text-primary' : 'text-muted-foreground'}`}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {/*
                `leading-tight` e não o `leading-none` padrão do `Label`: em
                360 px sobram ~196 px para este texto e ele quebra em duas
                linhas, que com `leading-none` ficam encostadas uma na outra.
                O rótulo NÃO leva `truncate` de propósito — texto cortado aqui
                esconderia justamente o que o interruptor faz (#52/#87).
              */}
              <Label htmlFor="auto-payment-detection" className="text-sm font-medium leading-tight">
                Detectar pagamento automaticamente
              </Label>
              <p className="text-xs text-muted-foreground">
                {resolution.dynamic ? 'QR exclusivo por orçamento' : 'QR da sua chave Pix'}
              </p>
            </div>

            {toggling ? (
              <Loader2 className="mt-1 h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
            ) : (
              <Switch
                id="auto-payment-detection"
                checked={enabled}
                disabled={!canToggle || loading}
                onCheckedChange={(next) => {
                  void setEnabled(next);
                }}
              />
            )}
          </div>

          <p className="mt-2 text-xs text-muted-foreground">
            {PIX_QR_MODE_HINTS[resolution.reason]}
          </p>

          {pendingSetup && (
            // Ligado e sem efeito ainda. O aviso é `warning` e não `error`
            // porque nada falhou — falta um passo que o usuário PODE dar, que é
            // exatamente a fronteira da taxonomia de toasts do app (#26).
            // Sobre a tinta âmbar, `text-warning-foreground` (branco puro) dava
            // contraste 1,08. Ver o hurdle #207.
            <p className="mt-2 flex items-center gap-1.5 rounded-lg bg-warning/10 px-2.5 py-2 text-xs font-medium text-amber-700 dark:text-amber-400">
              <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              Enquanto isso, seus orçamentos continuam com o QR da sua chave Pix.
            </p>
          )}

          {/*
            Atribuição obrigatória (Resolução Conjunta nº 16/2025): esta é a tela
            que LIGA a conta de pagamento, então é o primeiro ponto de contato em
            que o prestador precisa saber quem presta o serviço financeiro. Os
            canais são os do ASAAS de propósito — o suporte do app não resolve
            (nem enxerga) o que é da operação financeira.
          */}
          <div className="mt-3 border-t border-border pt-3">
            <AsaasSeal />
            <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
              {ASAAS_SHORT_DISCLOSURE}
            </p>
            <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
              Suporte do Asaas: {ASAAS_SUPPORT.phone} ({ASAAS_SUPPORT.phoneNote}) ·{' '}
              {ASAAS_SUPPORT.email}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
